import React from 'react';
import PropTypes from 'prop-types';
import { BANNER_STYLE, RAIL_STYLE } from './accentStyles';

/**
 * A bordered banner panel with the accent gradient running down its left edge.
 *
 * The caller supplies the body, so the same card serves a one-column or a two-column layout. The
 * design insets content 32px from the card's edges and the rail occupies the leftmost 16px of that,
 * so a body should carry 16px of left padding rather than a full 32px.
 */
export const GradientCard = ({ variant, children }) => {
  return (
    <section
      id="ai-assisted-search-banner"
      data-component="AiAssistedSearchBanner"
      data-testid="ai-assisted-search-banner"
      data-variant={variant}
      aria-labelledby="ai-assisted-search-banner-title"
      style={BANNER_STYLE}
      // `overflow-hidden` clips the accent rail to the card's radius, so the rail needs none itself.
      //
      // The results grid insets every pod by 4px a side, which the banner does not inherit: measured
      // against the rows above and below, it hung 4px past them left and right and left only their
      // 4px of clearance above and below. `sui-mx-1` is that same 4px, so the card lines up with the
      // pods, and `sui-my-3` adds to their 4px for the 16px separation the design has.
      className="sui-flex sui-overflow-hidden sui-rounded-lg sui-mx-1 sui-my-3"
    >
      {/* `sui-w-4` is 1.6rem, and Stencil sets the root font size to 62.5%, so this is the 16px
          the design specifies. */}
      <div aria-hidden="true" style={RAIL_STYLE} className="sui-w-4 sui-shrink-0" />
      {/* The border wraps the content only, never the rail: in the design the rail runs flush to
          the card's edge with no grey outlining it, and the top and bottom borders begin where it
          ends.

          Three sides as `sui-border-1` with the left switched off rather than as `sui-border-y-1`
          and `sui-border-r-1`: the page's Stencil sheet is built from what the site as a whole
          uses, and neither of those two is in it, so the border silently did not render at all.
          `sui-border-1` and `sui-border-l-0` are both there, and Tailwind's border-width plugin
          always emits the per-side rules after the all-sides one, so the left reliably wins. */}
      <div
        className={
          'sui-flex sui-grow sui-min-w-0 sui-rounded-r-lg'
          + ' sui-border-1 sui-border-l-0 sui-border-solid sui-border-primary'
        }
      >
        {children}
      </div>
    </section>
  );
};

GradientCard.displayName = 'GradientCard';

GradientCard.propTypes = {
  /**
   * Reported as `data-variant`, so QA and analytics can tell the layouts apart. Left as a free
   * string so retiring or adding a variant never means editing this block.
   */
  variant: PropTypes.string.isRequired,
  /** The card's body; supplies its own padding and the `-body` container id. */
  children: PropTypes.node,
};
